import { Popover, Tag } from "antd";
import React, { useEffect, useRef } from "react";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import OrderDTO from "@/dto/order/OrderDTO";
import { observer } from "mobx-react-lite";
import OrderSkuQuantity from "@/enum/OrderSkuQuantity";
import { collectEvent } from "@/services/dataTrack";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";

interface OrderFilterSelectProps {
    setLoading(loading: boolean): void;
    loading: boolean;
}

// 定义枚举到显示文本的映射
const orderSkuQuantityNameMap = {
    [OrderSkuQuantity.SINGLE_SKU_SINGLE_PIECE]: "单款单件",
    [OrderSkuQuantity.SINGLE_SKU_MULTI_PIECE]: "单款多件",
    [OrderSkuQuantity.MULTI_SKU]: "多款多件",
};

function calcFilteredOrders(cacheOrders: OrderDTO[], orderSortTypes: OrderSkuQuantity[]) {
    let hasSINGLE_SKU_SINGLE_PIECE = false;
    let hasSINGLE_SKU_MULTI_PIECE = false;
    let hasMULTI_SKU = false;

    orderSortTypes.forEach((orderSortType) => {
        if (orderSortType === OrderSkuQuantity.SINGLE_SKU_SINGLE_PIECE) {
            hasSINGLE_SKU_SINGLE_PIECE = true;
        }

        if (orderSortType === OrderSkuQuantity.SINGLE_SKU_MULTI_PIECE) {
            hasSINGLE_SKU_MULTI_PIECE = true;
        }

        if (orderSortType === OrderSkuQuantity.MULTI_SKU) {
            hasMULTI_SKU = true;
        }
    });

    const filteredOrders: OrderDTO[] = [];

    cacheOrders.forEach((order) => {
        const orderSkuQuantity = order.skuQuantity;

        if (hasSINGLE_SKU_SINGLE_PIECE && orderSkuQuantity === OrderSkuQuantity.SINGLE_SKU_SINGLE_PIECE) {
            filteredOrders.push(order);
        } else if (hasSINGLE_SKU_MULTI_PIECE && orderSkuQuantity === OrderSkuQuantity.SINGLE_SKU_MULTI_PIECE) {
            filteredOrders.push(order);
        } else if (hasMULTI_SKU && orderSkuQuantity === OrderSkuQuantity.MULTI_SKU) {
            filteredOrders.push(order);
        }
    });

    return filteredOrders;
}

const OrderFilterSelect: React.FC<OrderFilterSelectProps> = function (props) {
    const { setLoading, loading } = props;
    const { table } = useOrderManagementStore();
    const timer = useRef<NodeJS.Timeout>();

    function handleTagClick(tag: OrderSkuQuantity) {
        if (loading) return;

        const nextOrderSortTypes = table.productFilterStore.productFilterOrderSortTypes?.includes(tag)
            ? table.productFilterStore.productFilterOrderSortTypes.filter((it) => it !== tag)
            : [...table.productFilterStore.productFilterOrderSortTypes, tag];

        table.productFilterStore.setProductFilterOrderSortTypes(nextOrderSortTypes);

        if (!table.serverOrdersCache) return;

        setLoading(true);

        table.updateServerOrderCache();

        /** 等待Tag渐变过渡完毕 */
        timer.current = setTimeout(() => {
            if (nextOrderSortTypes.length) {
                const filteredOrders = calcFilteredOrders(table.serverOrdersCache, nextOrderSortTypes);

                table.setOrders(filteredOrders, false);
            } else {
                /** 还原回搜索时的订单 */
                table.setOrders(table.serverOrdersCache, false);
            }

            setLoading(false);
        }, 300);

        if (nextOrderSortTypes.length) {
            collectEvent(DataTrackEvent.PRODUCT_FILTER, {
                content: nextOrderSortTypes.map((type) => orderSkuQuantityNameMap[type]).join(),
            });
        }
    }

    useEffect(() => {
        return () => {
            if (timer.current) {
                clearTimeout(timer.current);
            }
        };
    }, []);

    return (
        <div className="mt5 mb5 fs12 flex flex-center">
            <div className="mr5 ml10">订单筛选:</div>
            <Popover
                content={
                    <span className="text-gray small">选择后会过滤出当前页对应的订单，其他订单不再显示，可多选</span>
                }
            >
                <div>
                    <Tag
                        className="order-sort-tag"
                        onClick={handleTagClick.bind(this, OrderSkuQuantity.SINGLE_SKU_SINGLE_PIECE)}
                        variant="outlined"
                        style={{ marginRight: 8 }}
                        color={
                            table.productFilterStore.productFilterOrderSortTypes.includes(
                                OrderSkuQuantity.SINGLE_SKU_SINGLE_PIECE,
                            )
                                ? "#DB9439"
                                : undefined
                        }
                    >
                        {orderSkuQuantityNameMap[OrderSkuQuantity.SINGLE_SKU_SINGLE_PIECE]}
                    </Tag>
                    <Tag
                        className="order-sort-tag"
                        onClick={handleTagClick.bind(this, OrderSkuQuantity.SINGLE_SKU_MULTI_PIECE)}
                        variant="outlined"
                        style={{ marginRight: 8 }}
                        color={
                            table.productFilterStore.productFilterOrderSortTypes.includes(
                                OrderSkuQuantity.SINGLE_SKU_MULTI_PIECE,
                            )
                                ? "#DB9439"
                                : undefined
                        }
                    >
                        {orderSkuQuantityNameMap[OrderSkuQuantity.SINGLE_SKU_MULTI_PIECE]}
                    </Tag>
                    <Tag
                        className="order-sort-tag"
                        onClick={handleTagClick.bind(this, OrderSkuQuantity.MULTI_SKU)}
                        variant="outlined"
                        style={{ marginRight: 8 }}
                        color={
                            table.productFilterStore.productFilterOrderSortTypes.includes(OrderSkuQuantity.MULTI_SKU)
                                ? "#DB9439"
                                : undefined
                        }
                    >
                        {orderSkuQuantityNameMap[OrderSkuQuantity.MULTI_SKU]}
                    </Tag>
                </div>
            </Popover>
        </div>
    );
};

export default observer(OrderFilterSelect);
